import { identity, keys, map, pickBy } from 'lodash';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { Observable, Subject, catchError, of, switchMap, take, takeUntil } from 'rxjs';
import { Component, OnInit, ChangeDetectorRef, Renderer2, PLATFORM_ID, Inject } from '@angular/core';

import { CatalogueCategorySeoType, InternalProductWithRatingsWithFavoriteStatusType } from '@sifca-monorepo/ecommerce-generator';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { AppService, AuthService, OrdersService, SeoService, SharedService, UtilsService } from '../../../services';

@Component({
  template: '',
})
export class BaseMenuComponent implements OnInit {
  protected unsubscribeAll: Subject<any> = new Subject<any>();

  seo: any;
  limit = 5;
  params: any;
  lastPage = 1;
  type = 'list';
  totalCount = 0;
  toggle = false;
  urlKey: string;
  perPage: number;
  searchTerm = '';
  pageTitle = 'List';
  orderBy = 'default';
  selectedCategory: any;
  authenticated: boolean;
  isButtonDisabled = false;
  categorySeo: CatalogueCategorySeoType;
  filtredProducts: InternalProductWithRatingsWithFavoriteStatusType[];
  initialProducts: InternalProductWithRatingsWithFavoriteStatusType[];
  loadingProducts$: Observable<boolean> = this.appService.loadingProducts$;

  constructor(
    protected router: Router,
    protected renderer: Renderer2,
    protected metaTagService: Meta,
    protected appService: AppService,
    protected titleTagService: Title,
    protected seoService: SeoService,
    protected authService: AuthService,
    protected utilsService: UtilsService,
    protected orderService: OrdersService,
    protected activeRoute: ActivatedRoute,
    protected translate: TranslateService,
    protected sharedService: SharedService,
    protected toastrService: ToastrService,
    protected changeDetectorRef: ChangeDetectorRef,
    @Inject(PLATFORM_ID) protected platformId: Object,
    @Inject(DOCUMENT) protected document: Document,
  ) {
    this.authService.authenticated$.pipe(takeUntil(this.unsubscribeAll)).subscribe((authenticated: boolean) => {
      this.authenticated = authenticated;
      this.changeDetectorRef.markForCheck();
    });
    this.activeRoute.queryParams.subscribe((params) => {
      if (params) {
        this.params = params;
        if (params['searchTerm']) {
          this.searchTerm = params['searchTerm'];
          this.appService.searchString = params['searchTerm'];
        } else {
          this.searchTerm = '';
          this.appService.searchString = '';
        }
      }
      if (!keys(params).length) {
        this.appService.selectedBrands = [];
        this.appService.selectedCategories = [];
        this.appService.productAttributes = [];
        this.appService.fromPrice = null;
        this.appService.toPrice = null;
      }
      this.refresh();
    });

    this.activeRoute.params.pipe(takeUntil(this.unsubscribeAll)).subscribe((params) => {
      this.urlKey = params['categoryUrl'];
      if (this.urlKey) {
        this.appService
          .getCategoryByUrl(params['categoryUrl'])
          .pipe(
            switchMap((result) => {
              this.selectedCategory = result;
              this.appService.selectedCategories = [result.id];
              this.categorySeo = result.seo;
              return this.seoService.getMainCategorySEOJSON(result.id);
            }),
          )
          .subscribe((res) => {
            this.seo = res;
            if (this.seo?.title) {
              this.titleTagService.setTitle(this.seo?.title);
            }
            this.metaTagService.addTags([
              ...map(this.seo?.metas, (meta) => pickBy(meta, identity)),
              { charset: 'UTF-8' },
              ...(this.categorySeo?.metaDesription ? [{ name: 'description', content: this.categorySeo?.metaDesription }] : []),
              { name: 'metaTitle', content: this.categorySeo?.metaTitle },
              { name: 'urlKey', content: this.categorySeo?.urlKey },
            ]);
            map(this.seo?.links, (seoLink) => {
              const link = this.renderer.createElement('link');
              if (seoLink.sizes) {
                link.sizes = seoLink.sizes;
              }
              if (seoLink.href) {
                link.href = seoLink.href;
              }
              if (seoLink.rel) {
                link.rel = seoLink.rel;
              }
              this.renderer.appendChild(this.document.head, link);
              return link;
            });
            if (!this.params?.page) {
              this.refresh();
            }
          });
      }
    });
  }

  ngOnInit(): void {
    this.appService.filtredProducts$.pipe(takeUntil(this.unsubscribeAll)).subscribe((paginateResponse) => {
      if (paginateResponse) {
        this.filtredProducts = paginateResponse.objects;
        this.totalCount = paginateResponse.count;
        this.perPage = this.appService?.productsLimit;
        if (paginateResponse.count >= this.limit) {
          this.lastPage = Math.ceil(paginateResponse?.count / (this.appService.productsLimit || this.perPage));
        } else {
          this.lastPage = 1;
        }
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  onCategoryChange(category) {
    this.selectedCategory = category;
    this.router.navigate([category?.seo?.urlKey ? '/c/' + category?.seo?.urlKey : '/menu']);
  }

  getGeneralSEOJSON() {
    this.seoService
      .getGeneralSEOJSON()
      .pipe(
        take(1),
        catchError(() => {
          return of(null);
        }),
      )
      .subscribe((res) => {
        this.seo = res;
        if (this.seo?.title) {
          this.titleTagService.setTitle(this.seo?.title);
        }
        this.sharedService.tags$ = [...this.metaTagService.addTags(map(this.seo?.metas, (meta) => pickBy(meta, identity))), { charset: 'UTF-8' }];
        this.sharedService.links$ = map(this.seo?.links, (seoLink) => {
          const link = this.renderer.createElement('link');
          if (seoLink.sizes) {
            link.sizes = seoLink.sizes;
          }
          if (seoLink.href) {
            link.href = seoLink.href;
          }
          if (seoLink.rel) {
            link.rel = seoLink.rel;
          }
          this.renderer.appendChild(this.document.head, link);
          return link;
        });
      });
  }

  refresh() {
    this.appService.productsPageIndex$ = this.params?.page - 1 || 0;
    this.urlKey = this.activeRoute.snapshot.paramMap.get('categoryUrl');
    if (!this.urlKey) {
      this.getGeneralSEOJSON();
    }
    if (this.urlKey && !this.appService.selectedCategories?.length) {
      return;
    }
    this.appService.getSimpleProductsWithRatingsWithProductAttributesAndFavoriteStatus().subscribe();
  }

  addToCart(product: InternalProductWithRatingsWithFavoriteStatusType, index: number) {
    this.isButtonDisabled = true;
    if (!this.authenticated) {
      if (this.appService.itemInCart(product.barcodes[0])) {
        const value: any = {
          barcode: { ...product.barcodes[0], internalProduct: { spces: product?.specs } },
          price: {
            unitGross: product.barcodes[0].price,
            gross: product.barcodes[0].price,
          },
          product,
          quantity: 1,
        };
        this.appService.updateLocalCart(value);
        this.appService.loadCart();
      } else {
        const value: any = {
          barcode: product.barcodes[0],
          price: {
            unitGross: product.barcodes[0].price,
            gross: product.barcodes[0].price,
          },
          product,
          quantity: 1,
        };
        this.appService.addToLocalCart(value);
        this.appService.loadCart();
      }
      this.appService.items$.pipe(takeUntil(this.unsubscribeAll)).subscribe((products: any[]) => {
        const allProducts = map(products, (product) => ({
          quantity: product.quantity,
          barcode: product?.barcode?.id,
        }));
        this.orderService.calculateShoppingCart(allProducts).subscribe();
        this.changeDetectorRef.markForCheck();
      });
      this.translate.get('SHARED.PRODUCT_ADDED_TO_CART').subscribe((text: string) => {
        this.toastrService.success(text);
      });
      this.isButtonDisabled = false;
    } else {
      const formValue = {
        barcode: product.barcodes[0].id,
        quantity: 1,
      };
      this.orderService
        .addProductsToCart([formValue])
        .pipe(
          catchError(() => {
            this.translate.get('SHARED.SOMETHING_WENT_WRONG').subscribe((text: string) => {
              this.toastrService.error(text);
            });
            return of(null);
          }),
        )
        .subscribe((res) => {
          if (res) {
            this.translate.get('SHARED.PRODUCT_ADDED_TO_CART').subscribe((text: string) => {
              this.toastrService.success(text);
            });
            this.isButtonDisabled = false;
          }
        });
      this.appService.loadCart();
    }
    this.changeDetectorRef.markForCheck();
  }
}
